import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {useMemo, useRef, type ReactNode} from 'react'
import {TaillightContext, type TaillightBlockContext} from './taillight-context'
import type {TaillightProps} from './taillight-types'
import {TaillightsGlyphs} from './taillights-glyphs'
import {useTaillightsController, useTaillightsRendering} from './taillights-provider'
import {taillightsRenderingStyle} from './taillights-rendering-styles'
import styles from './taillight.module.css'

/**
 * Keeps native source and child controls at stable positions across paint-mode changes.
 */
export function TaillightFrame({
  block,
  renderer = 'bitmap',
  inert = false,
  isSelectable = false,
  hidePaint = false,
  children,
  controls,
  className,
  style,
  'aria-label': ariaLabel,
}: Omit<TaillightProps, 'children'> & {
  children: ReactNode
  controls?: ReactNode
}) {
  const controller = useTaillightsController()
  const rendering = useTaillightsRendering()
  const profile = controller?.profile
  const nativeSourceRef = useRef<HTMLPreElement>(null)
  const accessibleName = ariaLabel?.trim() || undefined
  const css = renderer === 'css'
  const nativeSource = css || isSelectable || !inert
  const context = useMemo<TaillightBlockContext>(
    // eslint-disable-next-line @github-ui/github-monorepo/no-mixed-stability-context-value -- Controls need both the source ref and block metadata, so splitting would not prevent their updates. Image readiness stays separate.
    () => ({block, nativeSourceRef, renderer, inert, isSelectable, hidePaint}),
    [block, nativeSourceRef, renderer, inert, isSelectable, hidePaint],
  )
  return (
    <TaillightContext value={context}>
      <div
        {...testIdProps('taillight')}
        className={clsx(styles.Block, className)}
        style={{
          ...style,
          ...taillightsRenderingStyle({...rendering, ...profile, ...block.palette}),
          '--taillight-height': `${block.height}px`,
          minWidth: block.width,
          '--taillights-default-color': block.palette.defaultColor,
        }}
      >
        {children}
        {nativeSource && (
          <pre
            {...testIdProps(css ? 'taillights-chunk' : 'taillight-native-source')}
            ref={nativeSourceRef}
            translate="no"
            className={clsx(
              css ? styles.Paint : styles.Selectable,
              !isSelectable && styles.nonSelectable,
              hidePaint && styles.hiddenPaint,
            )}
            inert={inert || undefined}
            role={!inert && accessibleName ? 'region' : undefined}
            aria-label={!inert ? accessibleName : undefined}
            aria-hidden={inert || hidePaint || undefined}
            tabIndex={!inert && !hidePaint && isSelectable && accessibleName ? 0 : undefined}
          >
            <TaillightsGlyphs text={block.text} runs={block.glyphRuns} pseudoText={false} />
            {block.text.endsWith('\n') && <br inert aria-hidden />}
          </pre>
        )}
        {controls}
      </div>
    </TaillightContext>
  )
}
